import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkflowFaceConfig } from '../../lib/workflows/config';

/**
 * Photograph of the subject, taken from the operator's webcam.
 *
 * Carried over from v1, which customers have been asking after. Deliberately the same 640x480 frame v1
 * produced: it is what ATF has been accepting from those customers for years, and a photograph that
 * suddenly changes shape is a support ticket, not an improvement.
 *
 * The camera is a shared physical device. It is opened when this step is reached and released the
 * moment it is not needed — leaving a webcam light on after the operator has moved to another screen is
 * alarming in a room where someone is being fingerprinted.
 */

export const CAPTURE_WIDTH = 640;
export const CAPTURE_HEIGHT = 480;

interface Props {
  config: WorkflowFaceConfig;
  /** Whether this account actually has Secure Storage. */
  storageActive: boolean;
  /** Base64 PNG (no data-URL prefix), or null when nothing has been taken. */
  photo: string | null;
  onChange: (photo: string | null) => void;
  /** Save the photograph as a PNG. Named by the caller, which holds the subject's demographics. */
  onDownload: () => void;
  onBack: () => void;
  onNext: () => void;
}

type CamState = 'idle' | 'starting' | 'live' | 'denied' | 'unavailable';

export function FaceCapture({ config, storageActive, photo, onChange, onDownload, onBack, onNext }: Props) {
  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const [state, setState] = useState<CamState>('idle');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    setState('idle');
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      setError('This browser cannot open a camera. Chrome, Edge and Firefox all can.');
      return;
    }
    setState('starting');
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        // A hint, not a demand: a camera that cannot do this size negotiates the nearest it can, and
        // the canvas draws to the fixed output size regardless.
        video: { width: { ideal: CAPTURE_WIDTH }, height: { ideal: CAPTURE_HEIGHT } },
      });
      stream.current = s;
      if (video.current) {
        video.current.srcObject = s;
        await video.current.play().catch(() => undefined);
      }
      setState('live');
    } catch (e) {
      const name = (e as { name?: string })?.name ?? '';
      // A refused permission and an absent camera need different advice, so they are not merged.
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setState('denied');
        setError('Camera access was blocked. Allow it for this site in your browser settings, then try again.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setState('unavailable');
        setError('No camera was found. Connect one and try again.');
      } else {
        setState('unavailable');
        setError('The camera could not be opened. It may be in use by another application.');
      }
    }
  }, []);

  // Released on unmount, and whenever a photograph is being reviewed rather than taken.
  useEffect(() => stop, [stop]);
  useEffect(() => { if (photo) stop(); }, [photo, stop]);

  function take() {
    const v = video.current;
    if (!v) return;
    const canvas = document.createElement('canvas');
    canvas.width = CAPTURE_WIDTH;
    canvas.height = CAPTURE_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
    // Stored without the data-URL prefix, matching how fingerprint captures are held.
    onChange(canvas.toDataURL('image/png').split(',')[1] ?? null);
  }

  /*
   * Only what will actually happen. The workflow may ask for `storage`, but without Secure Storage on the
   * account there is nowhere to put it — promising it and then silently dropping the photograph is worse
   * than not offering it.
   */
  const effective = config.targets.filter((t) => t !== 'storage' || storageActive);
  const labels: Record<string, string> = {
    type10: 'included in the EFT',
    download: 'downloaded with the EFT',
    storage: 'kept in Secure Storage',
  };
  const targets = effective.map((t) => labels[t] ?? t).join(', ');
  const droppedStorage = config.targets.includes('storage') && !storageActive;

  return (
    <div className="panel">
      <div className="face-head">
        <div>
          <h3 className="review-h" style={{ margin: 0 }}>Photograph</h3>
          <p className="cart-note" style={{ textAlign: 'left', margin: '4px 0 0' }}>
            {config.required
              ? 'A photograph of the subject is required for this submission.'
              : 'Optional for this workflow. You can skip it and submit without one.'}
            {' '}{effective.length ? <>The photograph will be {targets}.</> : null}
            {droppedStorage && (
              <> This account does not have Secure Storage, so it will not be kept after this submission.</>
            )}
          </p>
        </div>
      </div>

      <div className="face-stage">
        {photo ? (
          <img className="face-shot" src={`data:image/png;base64,${photo}`} alt="Captured photograph of the subject" />
        ) : (
          <>
            <video
              ref={video}
              className="face-video"
              playsInline
              muted
              // Mirrored so the operator can frame the subject the way a mirror behaves. The CAPTURED
              // frame is drawn from the unmirrored source, so the photograph itself is not reversed.
              style={{ transform: 'scaleX(-1)', display: state === 'live' ? 'block' : 'none' }}
            />
            {state !== 'live' && (
              <div className="face-placeholder">
                {state === 'starting' ? 'Opening camera…' : error ?? 'The camera is off.'}
              </div>
            )}
          </>
        )}
      </div>

      {error && state !== 'starting' && <p className="cart-error" style={{ marginTop: 10 }}>{error}</p>}

      <div className="flow-actions">
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>

        {!photo && state !== 'live' && (
          <button className="btn btn-primary" onClick={() => void start()} disabled={state === 'starting'}>
            {state === 'starting' ? 'Opening…' : 'Open camera'}
          </button>
        )}
        {!photo && state === 'live' && (
          <>
            <button className="btn btn-ghost" onClick={stop}>Close camera</button>
            <button className="btn btn-primary" onClick={take}>Take photograph</button>
          </>
        )}
        {photo && (
          <>
            <button className="btn btn-ghost" onClick={() => { onChange(null); void start(); }}>Retake</button>
            {/*
              * Available as soon as the photograph exists, not only after submission. The operator often
              * needs the image for something else — an ATF portal upload, the customer's own file — and
              * until now the only copy arrived with the .eft at the very end of the flow, which is too
              * late if the transaction is abandoned or the photograph is all they came for.
              */}
            <button className="btn btn-ghost" onClick={onDownload}>Download PNG</button>
          </>
        )}

        {/*
          * Skipping is a first-class action, not a way out. Where the photograph is not part of the
          * submission the operator should not have to work out how to get past this screen — so the
          * control says what it does and sits where the next step would be.
          */}
        {!config.required && !photo && (
          <button className="btn btn-ghost" onClick={() => { stop(); onChange(null); onNext(); }}>
            Skip — no photograph
          </button>
        )}

        <button className="btn btn-primary" onClick={() => { stop(); onNext(); }} disabled={config.required && !photo}>
          Continue
        </button>
        {config.required && !photo && (
          <span className="flow-hint is-blocking">Take a photograph to continue.</span>
        )}
      </div>
    </div>
  );
}
